import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  UserPlus,
  Clock,
  CheckCircle2,
  RefreshCw,
  ChevronRight,
  Loader2,
  Info,
  Users
} from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

type SessionStatus =
  | 'unassigned'
  | 'no_progress'
  | 'in_progress'
  | 'ready_to_finalize'
  | 'no_answers';

interface AssignedSensor {
  id: string;
  email: string;
  full_name: string | null;
}

interface GradingSession {
  session_id: string;
  exam_set_id: string;
  exam_set_name: string | null;
  course_instance_id: string;
  course_instance_name: string;
  member_id: string | null;
  member_name: string | null;
  study_association_id: string | null;
  association_name: string | null;
  submitted_at: string | null;
  candidate_code: string | null;
  assigned_sensors: AssignedSensor[];
  total_answers: number;
  graded_answers: number;
  progress_pct: number;
  status: SessionStatus;
}

interface Summary {
  total: number;
  unassigned: number;
  no_progress: number;
  in_progress: number;
  ready_to_finalize: number;
  no_answers: number;
}

const STATUS_META: Record<SessionStatus, { label: string; color: string; icon: JSX.Element; help: string }> = {
  unassigned: {
    label: 'Sensor mangler',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: <UserPlus className="h-4 w-4" />,
    help: 'Kurset har ingen tildelt sensor — besvarelsen kan ikke sensureres før noen tildeles.'
  },
  no_progress: {
    label: 'Ikke påbegynt',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: <Clock className="h-4 w-4" />,
    help: 'Sensor er tildelt, men har ikke sensurert noen svar enda.'
  },
  in_progress: {
    label: 'Underveis',
    color: 'bg-kf-blue-100 text-kf-blue-800 border-kf-blue-200',
    icon: <Loader2 className="h-4 w-4" />,
    help: 'Sensor har startet — noen svar er sensurert, resten gjenstår.'
  },
  ready_to_finalize: {
    label: 'Klar til finalisering',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: <CheckCircle2 className="h-4 w-4" />,
    help: 'Alle svar er sensurert. Sensor må klikke "Fullfør sensur" for at resultat blir utstedt.'
  },
  no_answers: {
    label: 'Ingen svar',
    color: 'bg-slate-100 text-slate-800 border-slate-200',
    icon: <AlertCircle className="h-4 w-4" />,
    help: 'Besvarelsen har ingen registrerte svar. Vurder alternativ vurdering eller nullstilling.'
  }
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nb-NO', {
    timeZone: 'Europe/Oslo',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function GradingOverview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<GradingSession[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [filter, setFilter] = useState<SessionStatus | 'all'>('all');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch('/api/admin/grading-overview');
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (r.status === 403 && body.error === 'sensor_use_dashboard') {
          setError('Sensor-brukere skal bruke Sensordashboard for å sensurere.');
          return;
        }
        throw new Error(body.message || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setSessions(data.sessions || []);
      setSummary(data.summary || null);
    } catch (e: any) {
      setError(e.message || 'Kunne ikke hente oversikt');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const visible = filter === 'all' ? sessions : sessions.filter(s => s.status === filter);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Henter oversikt over besvarelser til sensur…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-start gap-2 text-red-800">
          <AlertCircle className="h-5 w-5 mt-0.5" />
          <div>
            <div className="font-semibold">Kunne ikke laste inn</div>
            <div className="text-sm mt-1">{error}</div>
          </div>
        </div>
        <button onClick={load} className="mt-3 btn btn-secondary btn-sm">
          <RefreshCw className="h-4 w-4 mr-1" /> Prøv igjen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Til sensur</h2>
          <p className="text-sm text-gray-600 mt-1">
            Oversikt over innleverte besvarelser som venter på sensur. Tildeling og selve sensuren
            håndteres av sensor via Sensordashboard.
          </p>
        </div>
        <button onClick={load} className="btn btn-secondary btn-sm">
          <RefreshCw className="h-4 w-4 mr-1" /> Oppdater
        </button>
      </div>

      {/* Summary chips (filter) */}
      {summary && summary.total > 0 && (
        <div className="flex flex-wrap gap-2 text-sm">
          <StatusChip
            active={filter === 'all'}
            label={`Alle (${summary.total})`}
            color="bg-gray-100 text-gray-800 border-gray-300"
            onClick={() => setFilter('all')}
          />
          {summary.unassigned > 0 && (
            <StatusChip
              active={filter === 'unassigned'}
              label={`Sensor mangler (${summary.unassigned})`}
              color={STATUS_META.unassigned.color}
              onClick={() => setFilter('unassigned')}
            />
          )}
          {summary.no_progress > 0 && (
            <StatusChip
              active={filter === 'no_progress'}
              label={`Ikke påbegynt (${summary.no_progress})`}
              color={STATUS_META.no_progress.color}
              onClick={() => setFilter('no_progress')}
            />
          )}
          {summary.in_progress > 0 && (
            <StatusChip
              active={filter === 'in_progress'}
              label={`Underveis (${summary.in_progress})`}
              color={STATUS_META.in_progress.color}
              onClick={() => setFilter('in_progress')}
            />
          )}
          {summary.ready_to_finalize > 0 && (
            <StatusChip
              active={filter === 'ready_to_finalize'}
              label={`Klar til finalisering (${summary.ready_to_finalize})`}
              color={STATUS_META.ready_to_finalize.color}
              onClick={() => setFilter('ready_to_finalize')}
            />
          )}
          {summary.no_answers > 0 && (
            <StatusChip
              active={filter === 'no_answers'}
              label={`Ingen svar (${summary.no_answers})`}
              color={STATUS_META.no_answers.color}
              onClick={() => setFilter('no_answers')}
            />
          )}
        </div>
      )}

      {/* Empty state */}
      {sessions.length === 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
          <div className="font-semibold text-emerald-900">Ingen besvarelser venter på sensur</div>
          <div className="text-sm text-emerald-800 mt-1">
            Alt er sensurert — eller det finnes ingen innleverte prøver enda.
          </div>
        </div>
      )}

      {/* Table */}
      {visible.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Kurs</th>
                <th className="px-4 py-3">Kandidat</th>
                <th className="px-4 py-3">Innlevert</th>
                <th className="px-4 py-3">Sensor</th>
                <th className="px-4 py-3">Progresjon</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm">
              {visible.map(s => {
                const meta = STATUS_META[s.status];
                return (
                  <tr key={s.session_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 align-top">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${meta.color}`}
                        title={meta.help}
                      >
                        {meta.icon}
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-gray-900">{s.course_instance_name}</div>
                      {s.exam_set_name && (
                        <div className="text-xs text-gray-500 mt-0.5">{s.exam_set_name}</div>
                      )}
                      {s.member_name && (
                        <div className="text-xs text-gray-400 mt-0.5">{s.member_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                        {s.candidate_code || '—'}
                      </code>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700 whitespace-nowrap">
                      {formatDate(s.submitted_at)}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {s.assigned_sensors.length === 0 ? (
                        <span className="text-red-600 text-xs italic">Ingen tildelt</span>
                      ) : (
                        <ul className="space-y-0.5">
                          {s.assigned_sensors.map(sen => (
                            <li key={sen.id} className="flex items-center gap-1 text-gray-700">
                              <Users className="h-3 w-3 text-gray-400" />
                              <span>{sen.full_name || sen.email}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${
                              s.progress_pct === 100
                                ? 'bg-emerald-500'
                                : s.progress_pct > 0
                                ? 'bg-kf-blue-500'
                                : 'bg-gray-300'
                            }`}
                            style={{ width: `${s.progress_pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600 tabular-nums">
                          {s.graded_answers}/{s.total_answers}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1 items-end">
                        {s.status === 'unassigned' && (
                          <Link
                            to={`/course-instances/${s.course_instance_id}?tab=sensors`}
                            className="text-xs font-medium text-red-700 hover:underline flex items-center gap-0.5"
                          >
                            Tildel sensor <ChevronRight className="h-3 w-3" />
                          </Link>
                        )}
                        <Link
                          to={`/course-instances/${s.course_instance_id}`}
                          className="text-xs font-medium text-kf-primary hover:underline flex items-center gap-0.5"
                        >
                          Åpne kurs <ChevronRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Filter viste ingen (men totalt > 0) */}
      {sessions.length > 0 && visible.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 flex items-center gap-2">
          <Info className="h-4 w-4" />
          Ingen besvarelser matcher valgt status. Fjern filteret for å se alle.
        </div>
      )}
    </div>
  );
}

function StatusChip({
  active,
  label,
  color,
  onClick
}: {
  active: boolean;
  label: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium transition ${
        active ? color + ' ring-2 ring-offset-1 ring-kf-primary' : color + ' opacity-70 hover:opacity-100'
      }`}
    >
      {label}
    </button>
  );
}
